import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { siteInfo, categories } from '@/mocks/manga';

const FORM_URL = 'https://readdy.ai/api/form/d9ontg8senbniolesii0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formEl = e.currentTarget;
    const honeypotEl = formEl.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      formEl.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const formData = new FormData(formEl);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') params.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请检查输入内容');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const pageTitle = `关于我们 - ${siteInfo.name} | 品牌介绍与联系方式`;
  const pageDesc = `${siteInfo.name}（${siteInfo.description}）品牌介绍、用户口碑与联系方式。致力于为腐漫爱好者提供最优质的阅读体验。`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": pageTitle,
      "description": pageDesc,
      "url": "https://fumanhua.com/about",
      "isPartOf": {
        "@type": "WebSite",
        "name": siteInfo.name,
        "url": "https://fumanhua.com"
      },
      "about": {
        "@type": "Organization",
        "name": siteInfo.name,
        "description": siteInfo.description,
        "url": "https://fumanhua.com",
        "foundingDate": siteInfo.foundingYear,
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": siteInfo.email,
          "availableLanguage": "Chinese"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://fumanhua.com" },
        { "@type": "ListItem", "position": 2, "name": "关于我们" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "腐漫画网提供哪些类型的腐漫？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "腐漫画网集结韩漫腐、韩漫耽美、日漫腐、国漫耽美等多种风格腐漫，涵盖校园、古风、奇幻、都市、武侠、电竞等多种题材，满足不同爱好者的需求。"
          }
        },
        {
          "@type": "Question",
          "name": "腐漫画网的漫画是否可以免费阅读？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "是的，腐漫画网所有漫画均提供免费全集下拉式阅读体验，无需付费即可畅享全部章节。"
          }
        },
        {
          "@type": "Question",
          "name": "腐漫画网多久更新一次漫画？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "腐漫画网每日更新最新腐漫资源，连载漫画通常每周更新一话，确保读者能够持续追更心仪的作品。"
          }
        },
        {
          "@type": "Question",
          "name": "如何找到自己喜欢的腐漫类型？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "您可以通过首页的分类导航进入韩漫腐、韩漫耽美、日漫腐、国漫耽美专区，也可以查看热门推荐和最新上架板块，快速发现优质腐漫作品。"
          }
        }
      ]
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="腐漫画网,关于我们,品牌介绍,联系方式,腐漫平台"
        canonical="https://fumanhua.com/about"
        twitterCard="summary"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            <section className="mb-14">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                关于 {siteInfo.name}
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-building-2-line text-xl"></i>
                  </div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">品牌介绍</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {siteInfo.name}成立于{siteInfo.foundingYear}年，是一个专注于腐漫推荐与阅读的综合性平台。
                    我们致力于为腐漫爱好者提供最全面、最优质的漫画资源，涵盖<strong>韩漫腐</strong>、<strong>韩漫耽美</strong>、
                    <strong>日漫腐</strong>、<strong>国漫耽美</strong>等主流腐漫类型，每日更新海量内容，
                    让每一位读者都能在这里找到心仪的作品。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3">
                    我们的团队由一群热爱腐漫文化的资深读者组成，深知腐漫爱好者的需求和偏好。
                    因此，我们精心筛选每一部作品，确保推荐给用户的都是高质量、好口碑的腐漫佳作。
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-heart-2-line text-xl"></i>
                  </div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">我们的使命</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们相信每一部腐漫都有其独特的魅力，每一个角色都值得被认真对待。
                    {siteInfo.name}致力于打造一个纯净、友善的腐漫阅读社区，让腐漫爱好者能够在这里自由交流、分享心得，
                    共同发现更多优秀的腐漫作品。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3">
                    我们坚持<strong>免费全集</strong>的阅读模式，让每一位读者都能无忧无虑地享受腐漫的乐趣，
                    不受付费墙的限制，真正做到"随心看"。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: siteInfo.stats.totalManga, label: '漫画资源' },
                  { value: siteInfo.stats.dailyVisitors, label: '日访问量' },
                  { value: siteInfo.stats.totalChapters, label: '更新章节' },
                  { value: siteInfo.stats.satisfaction, label: '用户好评' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 rounded-lg border border-background-200/70 bg-background-50">
                    <p className="text-xl md:text-2xl font-bold text-accent-600 font-heading">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  { name: '小樱花', text: '腐漫画网真的是我最爱的腐漫平台！分类清晰，更新又快，每天都要刷一刷看看有没有新的韩漫耽美更新。推荐给所有腐漫同好！' },
                  { name: '墨染星辰', text: '国漫耽美专区做得特别好，剑心如梦和月下长安都是在这里发现的宝藏作品。画风美，剧情赞，感谢腐漫画网的精心推荐！' },
                  { name: '东京塔下的猫', text: '日漫腐资源很全，东京恋语追了好久终于在这里找到完整版。全免费下拉式阅读体验超级棒，强烈推荐！' },
                  { name: '清风徐来', text: '从腐漫画网开始入坑腐漫，转眼已经两年了。平台一直在进步，漫画质量越来越高，是腐漫爱好者不可错过的好站。' },
                  { name: '樱花飘落', text: '韩漫腐和韩漫耽美的更新速度真的快，每周都能看到新鲜内容。界面简洁好看，阅读体验一流，已经成为我每天的必修课了。' },
                  { name: '夜雨江湖', text: '作为一个资深腐漫迷，腐漫画网给我的体验是最好的。分类细致，推荐精准，而且全免费！良心平台，必须支持。' },
                ].map((review, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-1 text-accent-500">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed flex-1">{review.text}</p>
                    <p className="text-xs text-foreground-400 font-medium">—— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">联系方式</h2>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-mail-send-line text-accent-500"></i>
                      <strong>邮箱：</strong>{siteInfo.email}
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-time-line text-accent-500"></i>
                      <strong>工作时间：</strong>周一至周五 9:00 - 18:00
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-information-line text-accent-500"></i>
                      <strong>反馈处理：</strong>我们通常会在24小时内回复您的邮件
                    </p>
                  </div>
                  <div className="mt-5">
                    <p className="text-sm text-foreground-500 mb-3">热门分类直达：</p>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="px-3 py-1 text-xs rounded-full border border-background-300/60 text-foreground-600 hover:text-accent-600 hover:border-accent-300 transition-colors whitespace-nowrap"
                        >
                          {cat.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-chat-3-line text-xl"></i>
                  </div>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">给我们留言</h2>
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="true">
                    <div>
                      <label htmlFor="about-name" className="block text-sm text-foreground-600 mb-1">您的称呼</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm text-foreground-600 mb-1">电子邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm text-foreground-600 mb-1">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-800 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-accent-500 text-background-50 font-medium rounded-full hover:bg-accent-600 transition-all duration-300 whitespace-nowrap text-sm"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-secondary-700 text-center">留言已发送，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-accent-600 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}